import { AccountType, accountTypeWithBorrower, getUserAccountTypes } from './user.type.constants'

export enum LoanStatusType {
  PREQUAL = 'prequal',
  APPSIGNED = 'appSigned',
  RESTRUCTURERELEASE = 'restructureRelease',
  LOAN_INTAKE_REVIEW = 'inTakeReview',
  LOAN_SETUP_ON_HOLD = 'loanSetupOnHold',
  APPLICATION = 'application',
  SUBMITTED = 'submitted',
  SUSPENDED = 'suspended',
  APPROVED = 'approved',
  RE_SUBMITTED = 'reSubmitted',
  UNDERWRITER_REVIEW = 'underwriterReview',
  DECLINED = 'declined',
  CLEAR_TO_CLOSE = 'clearToClose',
  READY_FOR_DOCS = 'readyForDocs',
  DOCS_OUT = 'docsOut',
  CLOSED = 'closed',
  CLOSEDLOANREVIEW = 'closedLoanReview',
  READYFORPURCHASE = 'readyForPurchase',
  FUNDED = 'funded',
  WITHDRAWN = 'withdrawn',
}

export const loanStatusConstants: {
  loanSetupUnHoldStatus: string[]
  closeStatus: string[]
  archivedStatus: string[]
  activeStatus: string[]
  suspendedStatus: string[]
  brokerCanResubmitConditions: string[]
  servicingStatus: Record<string, string>
  /** Servicing statuses the company counts as an active serviced loan (set from the app config). */
  activeServicingStatuses: string[]
} = {
  loanSetupUnHoldStatus: [],
  closeStatus: [],
  archivedStatus: [],
  activeStatus: [],
  suspendedStatus: [],
  brokerCanResubmitConditions: [],
  servicingStatus: {
    servicing: 'Servicing',
    reo: 'REO',
    purchased: 'Purchased',
    paidInFull: 'Paid in Full',
  },
  activeServicingStatuses: ['servicing', 'reo'],
}

export const defaultLoanServicingStatusKeys = Object.keys(loanStatusConstants.servicingStatus)

export const setLoanServicingStatus = (data: Record<string, string>) => {
  loanStatusConstants.servicingStatus = data
}

export const setActiveServicingStatuses = (data: string[]) => {
  if (Array.isArray(data)) loanStatusConstants.activeServicingStatuses = data
}

/** True when the status is one the company still treats as an active serviced loan. */
export const isActiveServicingStatus = (status: string) =>
  loanStatusConstants.activeServicingStatuses.includes(String(status || ''))

export const getLoanServicingStatus = (prefix = false) => {
  const { servicingStatus } = loanStatusConstants
  if (!prefix) return servicingStatus

  const result: Record<string, any> = {}
  Object.keys(servicingStatus).forEach((key) => {
    result[key] = `[Servicing] ${servicingStatus[key]}`
  })
  return result
}

export interface ILoanStatusConfig {
  fromPermission: AccountType[]
  toPermission: AccountType[]
  visibleTo?: string[]
  new: string
  origin: string
  use: boolean

  isActive?: boolean
  isArchived?: boolean
  isClose?: boolean
  isLoanSetupUnhold?: boolean
  isSuspended?: boolean
  isBrokerCanResubmitConditions?: boolean
  isIncludedCalculation?: boolean
  isIncludedProductionStatsCalculation?: boolean

  confirmRequired?: boolean
  confirmText1?: string
  hasAutoNote?: boolean

  /**
   * Loan channels (LoanType keys) this status appears on. Missing means every channel.
   * An empty list means none.
   */
  channels?: string[]
}

/**
 * Fallback when channel visibility is not loaded yet.
 * Prefer Object.keys(getLoanTypeMapForSelect()) at call sites so new statuses
 * default to In Use channels only.
 */
export const defaultLoanStatusChannels = () => ['0', '1', '2', '3', '4']

/** True when this status belongs on the status list for the loan's channel. */
export const statusAppliesToChannel = (
  item: Pick<ILoanStatusConfig, 'channels'> | undefined,
  channel: string | number | null | undefined,
) => {
  if (channel === null || channel === undefined || channel === '') return true
  const channels = item?.channels
  if (!Array.isArray(channels)) return true
  return channels.map(String).includes(String(channel))
}

export const defaultVisibleToAccountTypes = () => Object.keys(accountTypeWithBorrower)

const accountTypesHeld = (accountType: string | string[]) =>
  (Array.isArray(accountType) ? accountType : [accountType]).filter(Boolean)

/** Every type the profile holds. A status visible to any of them stays on the pipeline. */
export const getUserAccountTypeForVisibility = (profile: Record<string, any>): string[] => {
  if (!profile || profile.isBorrower || !profile.accountType) return ['borrower']
  const held = getUserAccountTypes(profile)
  return held.length ? held : ['borrower']
}

const statusVisibleToAccountTypes = (visibleTo: string[] | undefined, accountType: string | string[]) => {
  const types = accountTypesHeld(accountType)
  const allowed = visibleTo ?? defaultVisibleToAccountTypes()
  return types.some((type) => allowed.includes(type))
}

export const getVisibleLoanStatusesForAccountType = (
  config: Record<string, ILoanStatusConfig>,
  accountType: string | string[],
  labels: Record<string, string>,
) => {
  return Object.fromEntries(
    Object.entries(labels).filter(([key]) => statusVisibleToAccountTypes(config[key]?.visibleTo, accountType)),
  )
}

export const filterStatusByVisibility = (
  status: Record<string, boolean>,
  config: Record<string, ILoanStatusConfig>,
  accountType: string | string[],
) => {
  const filtered: Record<string, boolean> = {}
  Object.keys(status).forEach((key) => {
    if (statusVisibleToAccountTypes(config[key]?.visibleTo, accountType)) filtered[key] = status[key]
  })
  return filtered
}

export const setLoanStatusConfig = (data: Record<string, ILoanStatusConfig>) => {
  const activeStatus = Object.keys(data).filter((key) => data[key].use)

  loanStatusConstants.loanSetupUnHoldStatus = activeStatus.filter((key) => data[key].isLoanSetupUnhold)
  loanStatusConstants.closeStatus = activeStatus.filter((key) => data[key].isClose)
  loanStatusConstants.archivedStatus = activeStatus.filter((key) => data[key].isArchived)
  loanStatusConstants.activeStatus = activeStatus.filter((key) => data[key].isActive)
  loanStatusConstants.suspendedStatus = activeStatus.filter((key) => data[key].isSuspended)
  loanStatusConstants.brokerCanResubmitConditions = activeStatus.filter(
    (key) => data[key].isBrokerCanResubmitConditions,
  )
}
